import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch, FiAlertCircle } from "react-icons/fi";

interface NotFoundCardProps {
  title: string;
  description: string;
  /** Recovery action, rendered under the callout. Only pages that can offer a
   *  valid destination pass one — the chromeless fallback has no site to
   *  return to and no router to navigate with. */
  action?: ReactNode;
  /** Hide the "404" tracking label above the title. Callers that reuse this
   *  card for non-404 states (e.g. the CXA-703 disabled-WBE screen) pass false. */
  showStatusLabel?: boolean;
  /** Hide the "what now?" recovery callout. Callers that reuse this card for
   *  non-404 states pass false. */
  showWhatNowCallout?: boolean;
}

export const NotFoundCard = ({
  title,
  description,
  action,
  showStatusLabel = true,
  showWhatNowCallout = true,
}: NotFoundCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="w-full max-w-md">
      <div className="rounded-3xl border border-border bg-surface-card p-8 shadow-lg">
        {/* Icon and visual indicator */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-hover">
              <FiSearch className="h-8 w-8 text-ink/70" />
            </div>
            <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary">
              <FiAlertCircle className="h-4 w-4 text-on-primary" />
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="space-y-4 text-center">
          <div className="space-y-1">
            {showStatusLabel && (
              <p className="text-sm font-semibold tracking-[0.2em] text-ink-disabled">
                404
              </p>
            )}
            <h1 className="text-2xl font-bold text-ink">{title}</h1>
          </div>

          <p className="leading-relaxed text-ink/70">{description}</p>

          {showWhatNowCallout && (
            <div className="mt-6 rounded-lg border border-border bg-surface-page p-4">
              <p className="text-sm text-ink-tertiary">
                <strong>{t("error.not_found.what_now.title")}</strong>
              </p>
              <p className="mt-2 text-sm text-ink/70">
                {t("error.not_found.what_now.description")}
              </p>
            </div>
          )}

          {action}
        </div>
      </div>
    </div>
  );
};
